import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Session } from '../api/types.js';
import { useApiClient } from './use-api-client.js';

function hasActive(sessions: Session[] | undefined): boolean {
  if (!sessions) return false;
  return sessions.some((s) => s.status === 'processing' || s.status === 'waiting_for_input');
}

export function useSessions(serverId: string, enabled = true, status?: string) {
  const api = useApiClient();

  return useQuery({
    queryKey: ['sessions', serverId, status ?? 'default'],
    queryFn: () => api.sessions.list(serverId, status),
    enabled,
    refetchInterval: (query) =>
      hasActive(query.state.data) ? 5_000 : 10_000,
  });
}

/** What every session of the server waits for (#965), for the server page */
export function useServerWaits(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['server-waits', serverId],
    queryFn: () => api.sessions.serverWaits(serverId),
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 30_000 : 120_000),
  });
}

/** The session's open waits (#965), refreshed while it has some */
export function useSessionWaits(serverId: string, sessionId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['session-waits', serverId, sessionId],
    queryFn: () => api.sessions.waits(serverId, sessionId),
    refetchInterval: (query) => ((query.state.data?.length ?? 0) > 0 ? 30_000 : 120_000),
  });
}

export function useSession(serverId: string, sessionId: string) {
  const api = useApiClient();

  return useQuery({
    queryKey: ['session', serverId, sessionId],
    queryFn: () => api.sessions.get(serverId, sessionId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'processing' || status === 'waiting_for_input') return 3_000;
      return 30_000;
    },
  });
}

export function useCreateSession(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { model?: string; name?: string }) =>
      api.sessions.create(serverId, body),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', serverId] });
    },
  });
}

export function useDeleteSession(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) =>
      api.sessions.delete(serverId, sessionId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions', serverId] });
    },
  });
}

export function useRenameSession(serverId: string, sessionId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      api.sessions.rename(serverId, sessionId, name),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['session', serverId, sessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessions', serverId] });
    },
  });
}

/** A reviewed GitHub pull request, or a GitLab merge request (its link when the review has it) */
export type ReviewedRequest = { org: string; repo: string; number: string | number; url: string } | { project: string; iid: string; url?: string };

/**
 * Opens a session of the operator on a reviewed PR or MR: creates it, named after it, and sends a first message with
 * its reference and link only (its own texts are read by the session, not pasted). A main session: the operator's access, which
 * the sandboxed review session has not (cloud, cluster)
 */
export function useOpenPrSession(serverId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pr: ReviewedRequest) => {
      const mr = 'project' in pr;
      const session = await api.sessions.create(serverId, { name: mr ? `MR ${pr.project}!${pr.iid}` : `PR ${pr.org}/${pr.repo}#${pr.number}` });
      await api.sessions.prompt(serverId, session.id, (mr
        ? [
          `On travaille sur la MR GitLab ${pr.project}!${pr.iid}${pr.url ? ` : ${pr.url}` : ''}`,
          'aidev en a préparé une revue (page serveur, bloc « Pull request reviews »).',
          `Lis la MR, sa discussion et son diff (\`glab mr view ${pr.iid} -R ${pr.project} --comments\`, \`glab mr diff ${pr.iid} -R ${pr.project}\`), puis attends mes instructions.`,
        ]
        : [
          `On travaille sur la PR ${pr.org}/${pr.repo}#${pr.number} : ${pr.url}`,
          'aidev en a préparé une revue (page serveur, bloc « Pull request reviews »).',
          `Lis la PR, sa discussion et son diff (\`gh pr view ${pr.number} --repo ${pr.org}/${pr.repo} --comments\`, \`gh pr diff\`), puis attends mes instructions.`,
        ]).join('\n'));
      return session;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions', serverId] });
    },
  });
}
